import {Component, OnInit} from '@angular/core';
import {CoreService, ProleisObject, RequestParam, Task, TimetrackingsService, UserService} from "proleis-rest-client";
import {ActivatedRoute} from "@angular/router";
import {exhaustMap, forkJoin, Observable, switchMap} from "rxjs";
import {map, tap} from "rxjs/operators";
import {TrackingData} from "./complete-tracking/complete-tracking.component";
import {NotificationService} from "proleis-web-app";

@Component({
  selector: 'app-start-tracking',
  templateUrl: './start-tracking.component.html',
  styleUrls: ['./start-tracking.component.scss']
})
export class StartTrackingComponent implements OnInit {

  user$: Observable<ProleisObject>;
  task$: Observable<Task>;
  resources$: Observable<ProleisObject[]>;
  currentTracking$: Observable<ProleisObject>;
  currentMachineTracking$: Observable<ProleisObject>;
  serviceTypes$: Observable<ProleisObject[]>;
  ratingTypes$: Observable<ProleisObject[]>;

  selectedBookingMode: BookingMode = 'EMPLOYEE';
  selectedRes: ProleisObject;
  trackingDataMachine: TrackingData;
  trackingDataEmployee: TrackingData;
  machineWorkingtime = 0;
  comment = '';

  constructor(
    private coreService: CoreService,
    private timetrackingsService: TimetrackingsService,
    private userService: UserService,
    private activatedRoute: ActivatedRoute,
    private notificationService: NotificationService
  ) {
  }

  ngOnInit() {
    this.task$ = this.coreService.getObjectById<Task>(
      this.activatedRoute.snapshot.params.taskId
    );

    this.loadResources();
    this.currentTracking$ = this.loadCurrentTracking();
    this.currentMachineTracking$ = this.loadCurrentMachineTracking();
    this.user$ = this.userService.getCurrentUser();
    this.preselectResource();

    this.loadRatingTypes();
    this.loadServiceTypes();
  }


  loadResources() {
    this.resources$ = this.task$.pipe(
      switchMap(task => {
        const params: RequestParam = {
          TASK_ID: this.activatedRoute.snapshot.params.taskId,
          ELEMENT: 'RESOURCE_OBJECT_ID',
          BOOKING_TYPE: task.RESMACHINEBOOKING
        }
        return this.coreService.getObjectsQuery<ProleisObject[]>('getBDEPicklist', params);
      })
    )
  }

  loadCurrentTracking() {
    const aliasFields = 'RESNAME=RES_ID.TEXT;SERVICETYPENAME=SERVICE_TYPE_ID.TEXT;' +
      'TASKNAME=TASK_ID.TEXT;CREATORNAME=RUECKMELDER_ID.TEXT';
    return this.timetrackingsService.getCurrentUserTracking(aliasFields).pipe(
      map(trackings => trackings[0])
    );
  }

  loadCurrentMachineTracking() {
    const aliasFields = 'RESNAME=RES_ID.TEXT;SERVICETYPENAME=SERVICE_TYPE_ID.TEXT;' +
      'TASKNAME=TASK_ID.TEXT;CREATORNAME=RUECKMELDER_ID.TEXT';
    return this.task$.pipe(
      exhaustMap(task => {
        return this.timetrackingsService.getCurrentMachineTracking(task.RES_ID, aliasFields).pipe(
          map(trackings => trackings[0])
        );
      }),
      tap(tracking => console.log(tracking)),
    )
  }

  preselectResource() {
    const obs$ = forkJoin({
      task: this.task$,
      user: this.user$,
      resources: this.resources$
    });
    obs$.pipe(
      map(result => {
        if (result.task.RES_ID) {
          this.coreService.getObjectById<ProleisObject>(result.task.RES_ID).subscribe(res => {
            this.selectedRes = res;
          })
        } else {
          result.resources.find(res => res.OBJECT_ID === result.user.DEFAULT_RES_ID).subscribe(res => {
            this.selectedRes = res;
          })
        }
      })
    ).subscribe();
  }

  loadServiceTypes() {
    this.serviceTypes$ = this.task$.pipe(
      switchMap((task) => {
        const params: RequestParam = {
          TASK_ID: task.OBJECT_ID,
          ELEMENT: 'SERVICE_TYPE_OBJECT_ID',
          BOOKING_TYPE: this.selectedBookingMode === 'EMPLOYEE' ? 'EMPLOYEE' : 'MACHINE'
        };
        return this.coreService.getObjectsQuery<ProleisObject[]>(
          'getBDEPicklist',
          params,
        );
      }),
    );
  }

  loadRatingTypes() {
    this.ratingTypes$ = this.task$.pipe(
      switchMap((task) => {
        const params: RequestParam = {
          TASK_ID: task.OBJECT_ID,
          ELEMENT: 'TASK_OBJECT_ID',
          BOOKING_TYPE: this.selectedBookingMode === 'EMPLOYEE' ? 'EMPLOYEE' : 'MACHINE',
          RESOURCE_ID: this.selectedRes?.OBJECT_ID
        };
        return this.coreService.getObjectsQuery<ProleisObject[]>(
          'getBDEPicklist',
          params,
        );
      })
    );
  }

  compareRes(resA: ProleisObject, resB: ProleisObject) {
    return resA.OBJECT_ID === resB.OBJECT_ID;
  };

  startTracking() {
    this.user$.subscribe(user => {
      this.timetrackingsService.startTracking({
        taskId: this.activatedRoute.snapshot.params.taskId,
        resId: this.selectedRes?.OBJECT_ID,
        serviceTypeId: this.trackingDataEmployee?.serviceTypeId,
        ratingTypeId: this.trackingDataEmployee?.ratingTypeId,
        machineServiceTypeId: this.trackingDataMachine?.serviceTypeId,
        machineTaskId: this.trackingDataMachine?.ratingTypeId,
        comment: this.comment,
        machineWorkingTime: this.machineWorkingtime,
        manualMachineTracking: this.selectedBookingMode === 'EMPLOYEEANDMACHINE',
        userId: user?.OBJECT_ID
      }).subscribe(() => {
        this.notificationService.showNotification('Buchung gestartet.');
        history.back();
      });
    })
  }

  onEmployeeTrackingChanged(event: TrackingData) {
    this.trackingDataEmployee = event;
  }

  onMachineTrackingChanged(event: TrackingData) {
    this.trackingDataMachine = event;
  }

  onMachineWorkingtimeChanged(workingTime: number) {
    this.machineWorkingtime = workingTime;
  }
}

export type BookingMode = 'EMPLOYEE' | 'MACHINE' | 'EMPLOYEEANDMACHINE';
